import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { CommuteMapShell } from "@features/search/components/maps/commute-map/shell";
import {
  defaultScene,
  PersistentCommuteMapContext,
  type PersistentCommuteMapContextValue,
} from "@features/search/components/maps/persistent-commute-map-context";

function PersistentCommuteMapHost({
  hasMountedMap,
}: {
  hasMountedMap: boolean;
}) {
  const context = useContext(PersistentCommuteMapContext);
  const isActive = context?.isActive ?? false;

  if (!context) return null;

  const {
    scene,
    selectedCommuteId,
    setSelectedCommuteId,
    surfaceRect,
  } = context;

  const shouldRender = isActive || hasMountedMap;
  const wrapperStyle: CSSProperties = surfaceRect
    ? {
        top: `${surfaceRect.top}px`,
        left: `${surfaceRect.left}px`,
        width: `${surfaceRect.width}px`,
        height: `${surfaceRect.height}px`,
        opacity: isActive ? 1 : 0,
        visibility: isActive ? "visible" : "hidden",
        pointerEvents: isActive ? "auto" : "none",
      }
    : {
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        opacity: 0,
        visibility: "hidden",
        pointerEvents: "none",
      };

  return (
    <div
      className="fixed z-[var(--z-map)] overflow-hidden rounded-none transition-opacity duration-150 md:rounded-lg"
      style={wrapperStyle}
    >
      {shouldRender ? (
        <CommuteMapShell
          commutes={scene.commutes}
          userLocation={scene.userLocation}
          selectedCommuteId={selectedCommuteId}
          onSelectCommute={setSelectedCommuteId}
          onClearSelection={() => setSelectedCommuteId(null)}
          className="h-full w-full"
        />
      ) : null}
    </div>
  );
}

export function PersistentCommuteMapProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [scene, setScene] = useState(defaultScene);
  const [selectedCommuteId, setSelectedCommuteId] = useState<string | null>(
    null,
  );
  const [surfaceRect, setSurfaceRect] = useState<
    PersistentCommuteMapContextValue["surfaceRect"]
  >(null);
  const [isActive, setIsActiveState] = useState(false);
  const [hasMountedMap, setHasMountedMap] = useState(false);
  const [showRecenter, setShowRecenter] = useState(false);
  const recenterHandlerRef = useRef<(() => void) | null>(null);

  // Latching on activation keeps the MapLibre instance mounted once it has been
  // used, so returning to a map page does not pay re-initialization cost.
  const setIsActive = useCallback((active: boolean) => {
    setIsActiveState(active);
    if (active) setHasMountedMap(true);
  }, []);

  const registerRecenterHandler = useCallback(
    (handler: (() => void) | null) => {
      recenterHandlerRef.current = handler;
    },
    [],
  );

  const recenterMap = useCallback(() => {
    recenterHandlerRef.current?.();
    setShowRecenter(false);
  }, []);

  const value = useMemo<PersistentCommuteMapContextValue>(
    () => ({
      scene,
      selectedCommuteId,
      surfaceRect,
      isActive,
      showRecenter,
      setShowRecenter,
      registerRecenterHandler,
      recenterMap,
      setScene,
      setSelectedCommuteId,
      setSurfaceRect,
      setIsActive,
    }),
    [
      scene,
      selectedCommuteId,
      surfaceRect,
      isActive,
      showRecenter,
      registerRecenterHandler,
      recenterMap,
      setIsActive,
    ],
  );

  return (
    <PersistentCommuteMapContext.Provider value={value}>
      <PersistentCommuteMapHost hasMountedMap={hasMountedMap} />
      {children}
    </PersistentCommuteMapContext.Provider>
  );
}
